import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apolloClient } from '../../../services/graphql';
import { GET_MY_MODERATOR_PERMISSIONS } from '../../../graphql/user-customer-access';
import { useAuth } from '../../../contexts/AuthContext';

interface CustomerPermissionGrant {
  canAdd: boolean;
  canChange: boolean;
  canDelete: boolean;
  customer: { id: string; name: string };
}

export function useModeratorPermissions() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMINISTRATOR';

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['myModeratorPermissions', user?.id],
    enabled: !!user && !isAdmin,
    queryFn: async () => {
      const { data: result } = await apolloClient.query({
        query: GET_MY_MODERATOR_PERMISSIONS,
        fetchPolicy: 'network-only',
      });
      return result.myModeratorPermissions as {
        grants: CustomerPermissionGrant[];
        canManageConditions: boolean;
      };
    },
    refetchOnWindowFocus: true,
  });

  const grantMap = useMemo(() => {
    const map = new Map<string, CustomerPermissionGrant>();
    for (const grant of data?.grants ?? []) {
      map.set(grant.customer.id, grant);
    }
    return map;
  }, [data?.grants]);

  const canAdd = (customerId?: string | null) => {
    if (isAdmin) return true;
    if (!customerId) return false;
    return grantMap.get(customerId)?.canAdd ?? false;
  };

  const canChange = (customerId?: string | null) => {
    if (isAdmin) return true;
    if (!customerId) return false;
    return grantMap.get(customerId)?.canChange ?? false;
  };

  const canDelete = (customerId?: string | null) => {
    if (isAdmin) return true;
    if (!customerId) return false;
    return grantMap.get(customerId)?.canDelete ?? false;
  };

  const canAccessAppointmentDetails =
    isAdmin ||
    (data?.grants ?? []).some(
      (grant) => grant.canAdd || grant.canChange || grant.canDelete
    );

  const canManageConditions = isAdmin ? true : (data?.canManageConditions ?? false);

  return {
    isLoading: !isAdmin && isLoading,
    canAdd,
    canChange,
    canDelete,
    canAccessAppointmentDetails,
    canManageConditions,
    refetch,
  };
}
